import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';

const paymentsAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
});

paymentsAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

paymentsAPI.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  },
);

const BASE = '/payments';

const paymentsService = {
  async listChallans(params = {}) {
    try {
      const res = await paymentsAPI.get(`${BASE}/challans`, { params });
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async getSummary(params = {}) {
    try {
      const res = await paymentsAPI.get(`${BASE}/summary`, { params });
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async getHistory(subOrderId) {
    try {
      const res = await paymentsAPI.get(`${BASE}/challans/${subOrderId}/payments`);
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async recordPayment(subOrderId, payload) {
    try {
      const res = await paymentsAPI.post(`${BASE}/challans/${subOrderId}/payments`, payload);
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async reversePayment(paymentId) {
    try {
      const res = await paymentsAPI.delete(`${BASE}/payments/${paymentId}`);
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async updateDueDate(subOrderId, payload) {
    try {
      const res = await paymentsAPI.patch(`${BASE}/challans/${subOrderId}/due-date`, payload);
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // ---- bulk ----
  // mark-paid and due-date are transactional server-side: the whole batch lands or
  // none of it does. Reminders are not — a sent message cannot be recalled — so that
  // one reports per-challan outcomes. All three return the same {requested, applied,
  // skipped, failed, results} envelope.
  async bulkMarkPaid(payload) {
    try {
      const res = await paymentsAPI.post(`${BASE}/bulk/mark-paid`, payload);
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async bulkUpdateDueDate(payload) {
    try {
      const res = await paymentsAPI.post(`${BASE}/bulk/due-date`, payload);
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async bulkSendReminders(payload) {
    try {
      const res = await paymentsAPI.post(`${BASE}/bulk/reminder`, payload);
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async sendReminder(subOrderId, channels) {
    try {
      const res = await paymentsAPI.post(`${BASE}/challans/${subOrderId}/reminder`, { channels });
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },
};

export default paymentsService;
